import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  GTT_TARGET_PRICE,
  GTT_TARGET_QUANTITY,
  GTT_INTRO_SEEN,
  TRIGGER_ORDER_WIZARD,
  WIZARD_DATA,
} from '../../constants/sessionStorage';
import StepByStep from '../StepByStep';
import BasicModal from '../BasicModal/BasicModal';
import { ModalCloseIcon, wizardHeaderCss, wizardTitleCss } from './Common';
import config from '../../config';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { deleteWizardKey } from '../../util/helpers/wizardHelpers';
import TargetPriceProductSelection from './TargetPriceProductSelection';
import TargetPriceOrderEntry from './TargetPriceOrderEntry';
import TargetPriceOrderReview from './TargetPriceOrderReview';
import TargetPriceOrderConfirmation from './TargetPriceOrderConfirmation';
import TargetPriceInfoDrawer from './TargetPriceInfoDrawer';

// Buy-at-Target-Price (GTT / trigger-order) modal flow. Mirrors PriceAlertFlow:
// a BasicModal shell hosting a StepByStep wizard, all four screens
// (TargetPriceProductSelection, TargetPriceOrderEntry, TargetPriceOrderReview,
// TargetPriceOrderConfirmation) implemented.
const GTTFlow = ({ isModalOpen, handleCloseModal, onSwitchToBuyFlow }) => {
  const { brokerTheme } = config;
  const isGS = brokerTheme === 'GoldSilver';

  const [wizardData, setWizardData] = useState(
    () => retrieveFromSessionStorage(WIZARD_DATA) || {},
  );
  const [wizardTitle, setWizardTitle] = useState('');
  const [goBackHandler, setGoBackHandler] = useState(null);
  const [closeHandler, setCloseHandler] = useState(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  // Auto-open the "How Buy at Target Price Works" intro once per flow session,
  // whenever the flow opens — regardless of the entry surface (Header link,
  // Buy page, product card) or which step it lands on. Gated by GTT_INTRO_SEEN,
  // which closeModal clears so a freshly reopened flow shows it again.
  useEffect(() => {
    if (isModalOpen && !retrieveFromSessionStorage(GTT_INTRO_SEEN)) {
      setIsInfoOpen(true);
    }
  }, [isModalOpen]);

  const handleInfoClose = () => {
    storeInSessionStorage(GTT_INTRO_SEEN, true);
    setIsInfoOpen(false);
  };

  // BasicModal only locks <body>, which doesn't reliably stop the viewport
  // scroll on mobile (the page behind kept its own scrollbar). The viewport
  // scrollbar derives from the root <html> element, so lock overflow there
  // while the modal is open and restore the previous value on close.
  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }
    const { style } = document.documentElement;
    const previousOverflow = style.overflow;
    style.overflow = 'hidden';
    return () => {
      style.overflow = previousOverflow;
    };
  }, [isModalOpen]);

  const TriggerOrderSteps = [
    { children: TargetPriceProductSelection },
    { children: TargetPriceOrderEntry },
    { children: TargetPriceOrderReview },
    { children: TargetPriceOrderConfirmation },
  ];

  const setDataForWizard = (value) => {
    setWizardData((prev) => {
      const next = { ...prev, ...value };
      storeInSessionStorage(WIZARD_DATA, next);
      return next;
    });
  };

  const closeModal = () => {
    handleCloseModal();
    removeFromSessionStorage(WIZARD_DATA);
    removeFromSessionStorage(GTT_TARGET_PRICE);
    removeFromSessionStorage(GTT_TARGET_QUANTITY);
    removeFromSessionStorage(GTT_INTRO_SEEN);
    // StepByStep persists the current step inside the nested WIZARD object, so
    // clear it there (removeFromSessionStorage only touches top-level keys) to
    // avoid leaking the step into the next time the flow opens.
    removeFromSessionStorage(TRIGGER_ORDER_WIZARD);
    // StepByStep persists the current step inside the nested WIZARD object, which
    // removeFromSessionStorage (top-level keys only) doesn't reach — clear it
    // there too so the step doesn't leak into the next time the flow opens.
    deleteWizardKey(TRIGGER_ORDER_WIZARD);
  };

  const handleBackButtonClick = () => {
    if (goBackHandler) {
      goBackHandler();
    }
  };

  const goBackFn = goBackHandler ? handleBackButtonClick : undefined;

  return (
    <BasicModal
      title={wizardTitle}
      size="lg"
      isOpen={isModalOpen}
      close={closeHandler || closeModal}
      closeIconVisible
      goBack={goBackFn}
      closeIcon={<ModalCloseIcon />}
      fixedHeight
      smallTitle
      headerCss={wizardHeaderCss}
      titleCss={wizardTitleCss}
      darkArrow
    >
      <StepByStep sessionScopeStep={TRIGGER_ORDER_WIZARD} fitParentHeight>
        {({ goStepForward, goStepBack, skipPreviousStep, currentStepIndex }) =>
          TriggerOrderSteps.map((step, index) =>
            React.createElement(step.children, {
              stepIndex: index,
              currentStepIndex,
              // StepByStep's own isLastStep is unreliable in function-as-child
              // mode (it reads the callback's arity, not the step count), so
              // compute it from the steps array. Screens use it to avoid
              // advancing past the last defined step while later screens are
              // still unimplemented.
              isLastStep: currentStepIndex >= TriggerOrderSteps.length - 1,
              handleBack: goStepBack,
              handleNext: goStepForward,
              skipPreviousStep,
              closeModal,
              wizardData,
              setWizardData: setDataForWizard,
              setWizardTitle,
              setGoBackHandler,
              setCloseHandler,
              isGS,
              onSwitchToBuyFlow,
            }),
          )
        }
      </StepByStep>
      <TargetPriceInfoDrawer isOpen={isInfoOpen} onClose={handleInfoClose} />
    </BasicModal>
  );
};

GTTFlow.propTypes = {
  isModalOpen: PropTypes.bool,
  handleCloseModal: PropTypes.func,
  onSwitchToBuyFlow: PropTypes.func,
};

export default GTTFlow;
